<template>
  <div ref="stackBarCanvas">
    <ClientOnly  >
      <template  v-if="tooltip.tooltipPosition.value.owner===tooltipId">
        <Teleport to="#teleport-tooltipchart">
          <div class="text-2xl text-Primary-800">
            <span class="font-bold" v-if="hoveredData.serieId">{{ hoveredData.serieId }}: </span
            > 
            <span class="font-bold">{{ hoveredData.value }}</span>
            <span v-if="hoveredData.percentage" class="pl-2">
              {{ hoveredData.percentage }}
            </span>
          </div>
          <div class="text-sm">{{ hoveredData.childrenId }}</div>
        </Teleport>

        
      </template>
    </ClientOnly>

    <div
      class="relative"
      :class="showLegend ? 'grid grid-cols-5' : ''"
      ref="svgwrapper"
    >
      <div class="col col-span-4">
        <svg ref="svg" class="" :width="availableWidth" :height="canvasHeight">
          <g
            v-for="(serie, i) in series"
            :key="serie.id"
            :transform="`translate(0, ${
              i * barHeight * 1.1 + (i === series.length - 1 ? avgSpacing : 0)
            })`"
          >
            <rect
              v-for="(d, stackIndex) in serie.children"
              :key="serie.id + stackIndex"
              :x="getXPos(stackIndex, serie.children)"
              :y="0"
              :rx="roundedRadius"
              :ry="roundedRadius"
              :data-id="d.id"
              :data-stackindex="stackIndex"
              :width="widthScale(d.value)"
              :height="barHeight"
              :fill="colorScale(d.id)"
              :class="[
                rectClass,
                hoveredLegendId !== '' && hoveredLegendId !== d.id
                  ? 'opacity-10'
                  : '',
              ]"
              @mouseover="hoveredItemHandler($event, d, serie.id)"
              @mouseleave="hoveredItemHandler($event, null)"
            />

            <template v-if="showFigures">
              <text
                v-for="(d, stackIndex) in serie.children"
                v-show="widthScale(d.value) > 40"
                :key="'text' + serie.id + stackIndex"
                class="pointer-events-none text-xs fill-white font-semibold mt-1 ml-1 overflow-x-hidden"
                :x="getXPos(stackIndex, serie.children)"
                :y="barHeight / 2"
                dy="0.25rem"
                dx="0.25rem"
                :style="{
                  width: widthScale(d) + 'px',
                  height: barHeight + 'px',
                }"
              >
                {{ $formater.salaryFormater(d.value) }}
              </text>
            </template>

            <!-- main text legend for bar-->
            <text
              v-if="showLabels"
              text-anchor="start"
              font-size="11px"
              :x="0"
              :y="barHeight / 2"
              :dy="'.35em'"
              class="fill-softBlack"
              :class="{
                italic: serie.id === 'Average',
                'font-thin': serie.id === 'Average',
              }"
            >
              {{ serie.id }}
            </text>
          </g>
          <!-- x axis -->
          <g
            id="axis"
            :transform="`translate(0,${canvasHeight - barHeight})`"
            v-if="showLabels"
          >
            <g
              v-for="(d, i) in widthScale.ticks(4)"
              :key="i"
              :transform="`translate(${axisWidth + widthScale(d)}, 0)`"
            >
              <line
                x1="0"
                y1="0"
                x2="0"
                :y2="4"
                stroke="black"
                stroke-width="1"
                stroke-dasharray="2,2"
              />
              <text
                x="0"
                y="0"
                dy="1.2em"
                text-anchor="start"
                font-size="11px"
                class="fill-softBlack"
              >
                {{ $formater.salaryFormater(d) }}
              </text>
            </g>
          </g>
        </svg>
      </div>
      <div class="col-span-1" v-if="showLegend">
        <div
          class="mt-4 md:mt0 flex flex-row md:flex-col gap-1 justify-between md:justify-start"
        >
          <div
            v-for="catName in allSubCategoryNames"
            :key="catName"
            class="flex justify-start gap-4"
            @mouseover="hoveredLegendId = catName"
            @mouseleave="hoveredLegendId = ''"
          >
            <div
              :style="{ backgroundColor: colorScale(catName) }"
              class="w-4 h-4 shrink-0"
            />
            <div class="text-xs">{{ catName }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { scaleLinear, scaleOrdinal, sum, max, schemeTableau10 } from "d3";

export default {
  setup() {
    const tooltip = useTooltip();
    const svgwrapper = ref(null);
    const { width: availableWidth, height } = useElementSize(svgwrapper);
    return { tooltip, svgwrapper, availableWidth };
  },
  props: {
    series: {
      type: Array,
      default: () => [
        {
          id: "2010",
          children: [
            {
              id: "1",
              value: 0.1,
            },
            {
              id: "2",
              value: 0.2,
            },
          ],
          value: 0.3,
        },
        {
          id: "2011",
          children: [
            {
              id: "1",
              value: 0.4,
            },
            {
              id: "2",
              value: 0.5,
            },
          ],
          value: 0.9,
        },
      ],
    },
    colors_cat: {
      type: Array,
      default: () => schemeTableau10,
    },

    isNotOrderable: {
      type: Boolean,
      default: false,
    },
    hasAverageSpecial: {
      type: Boolean,
      default: true,
    },
    showFigures: {
      type: Boolean,
      default: false,
    },
    axisWidth: {
      type: Number,
      default: 50,
    },
    showLegend: {
      type: Boolean,
      default: true,
    },
    barHeight: {
      type: Number,
      default: 24,
    },
    showLabels: {
      type: Boolean,
      default: true,
    },
    colorScaleExternal: {
      type: Function,
      default: null,
    },
    rectClass: {
      type: String,
      default: "stroke-white stroke-2 hover:brightness-110 ",
    },
    roundedRadius: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      avgSpacing: 6,
      scaleWidth: 0,
      hoveredData: {},
      hoveredLegendId: "",
      tooltipId: "stacked-horizontal-tooltip",
    };
  },

  computed: {
    spaceForLegend() {
      return this.showLegend ? 0.3 : 0;
    },
    maxSeriesData() {
      return max(this.series, (d) => d.value);
    },
    widthScale() {
      return scaleLinear()
        .domain([0, this.maxSeriesData])
        .range([0, this.scaleWidth * (1 - this.spaceForLegend)])
        .clamp(true);
    },
    canvasHeight() {
      if (this.showLabels)
        return (
          (this.series.length + 1) * this.barHeight * 1.1 + this.avgSpacing
        );
      else return this.series.length * this.barHeight * 1.1;
    },
    colorScale() {
      if (this.colorScaleExternal !== null) return this.colorScaleExternal;
      else {
        const colorArray = this.colors_cat;
        return scaleOrdinal()
          .domain(this.allSubCategoryNames)
          .range(colorArray);
      }
    },
    allSubCategoryNames() {
      const allnames = this.series.flatMap((d) => d.children.map((c) => c.id));
      // remove duplicates
      return [...new Set(allnames)];
    },
  },
  mounted() {
    this.tooltipId = this.tooltipId + Math.random().toString(36).substr(2, 9);
    this.$nextTick(() => {
      // run this code once 500ms after the component is mounted
      setTimeout(() => {
        this.scaleWidth = this.availableWidth;
      }, 600);
    });
    // update scaleWidth when the window is resized
  },
  // destory listener when component is destroyed
  unmounted() {},

  methods: {
    getXPos(stackIndex, stackElements) {
      // get the sum of all items in the stack before the current item defined by stackIndex
      const sumOfPreviousStackElements = sum(
        stackElements.slice(0, stackIndex).map((d) => d.value)
      );
      // return the x position of the current item
      return this.axisWidth + this.widthScale(sumOfPreviousStackElements);
    },
    convertRemToPixels(rem) {
      if (typeof window !== "undefined")
        return (
          rem * parseFloat(getComputedStyle(document.documentElement).fontSize)
        );
      else return rem * 16;
    },
    hoveredItemHandler(event, d, serieId) {
      
      if (d === null) {
        this.tooltip.hide();
      } else {
        this.hoveredData = {
          serieId,
          childrenId: d.id,
          value: this.$formater.salaryFormater(d.value),
          percentage: d.valueRelative
            ? `${this.$formater.intFormater(d.valueRelative * 100)}%`
            : "",
        };
        this.tooltip.show(event.pageX, event.pageY,this.tooltipId,'4chart');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
rect {
  transition: y 0.5s, height 0.5s, width 1s, opacity 0.2s, filter 0.4s;
}
</style>
